import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  Calendar, 
  LayoutDashboard, 
  PlusCircle, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Filter, 
  LogOut, 
  ChevronLeft, 
  ChevronRight, 
  Info, 
  FileText, 
  Download, 
  Trash2,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Loader2,
  Mail,
  Lock,
  X
} from 'lucide-react';

// Firebase imports
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, updateDoc, setDoc, doc, deleteDoc, query, orderBy, onSnapshot } from 'firebase/firestore';

// Assets & Data
import { LOGO_LEFT, LOGO_RIGHT } from './assets/pdfAssets';
import * as Icons from './components/Icons';
import SchedulePanel from './components/SchedulePanel';
import { INITIAL_ASSESSMENTS, RULES, TRIMESTERS, SUBJECTS } from './data/scheduleData';

// O Firebase agora é provido inteiramente via props (propAuth, propDb) pelo Planner.js,
// garantindo que não há sobrescrita do aplicativo ou chaves do Firebase.
let app, auth, db; // Mantido para referência de fallback (undefined), mas as props serão sempre usadas


// Simple UI Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm p-6 border border-slate-100 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false, type = "button" }) => {
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 active:scale-95",
    secondary: "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50",
    ghost: "bg-transparent text-slate-600 hover:bg-slate-100",
    danger: "bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100"
  };
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    rose: "bg-rose-50 text-rose-600 border-rose-100",
    slate: "bg-slate-100 text-slate-600 border-slate-200"
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${colors[color]}`}>
      {children}
    </span>
  );
};

function SchoolSyncVA({ auth: propAuth, db: propDb, session: propSession, userRole }) {
  const [view, setView] = useState(userRole === 'master' ? 'admin' : 'dashboard');
  const [user, setUser] = useState(propSession || null);
  const [loading, setLoading] = useState(!propSession);
  const [assessments, setAssessments] = useState([]);
  const [rules, setRules] = useState([]);
  const [editingAssessment, setEditingAssessment] = useState(null);

  // true para Coordenação Geral (role 'master'), false para professores
  const isCoordinator = userRole === 'master';
  // Use props if provided, otherwise fallback to local instances
  const authInstance = propAuth || auth;
  const dbInstance = propDb || db;

  // Application ID for isolated collections (multi-tenant support)
  const appId_va = "eleva-barra";

  useEffect(() => {
    if (propSession) {
      setUser(propSession);
      setLoading(false);
      return;
    }
    const unsubAuth = onAuthStateChanged(authInstance, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubAuth();
  }, [propSession, authInstance]);

  useEffect(() => {
    // If we have a user and we're not in mock mode
    const isMock = authInstance?.app?.options?.apiKey === 'mock-key';
    
    if (!user && !isMock) return;

    // Load mock data if in mock mode
    if (isMock) {
      setAssessments(INITIAL_ASSESSMENTS);
      setRules(RULES);
      return;
    }

    // Real-time synchronization for assessments
    const qA = query(collection(dbInstance, 'artifacts', appId_va, 'public', 'data', 'assessments'), orderBy('createdAt', 'desc'));
    const unsubA = onSnapshot(qA, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAssessments(data);
    });

    // Real-time synchronization for rules
    const qR = query(collection(dbInstance, 'artifacts', appId_va, 'public', 'data', 'rules'));
    const unsubR = onSnapshot(qR, (snapshot) => {
        const dbRules = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Mescla as regras do Firestore com as regras padrão (RULES)
        const mergedRules = RULES.map(defaultRule => {
          const dbRule = dbRules.find(r => r.id === defaultRule.id || r.grade === defaultRule.grade);
          return dbRule ? { ...defaultRule, ...dbRule } : defaultRule;
        });
        setRules(mergedRules);
    });

    return () => {
      unsubA();
      unsubR();
    };
  }, [user, dbInstance, authInstance]);

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este registro?")) return;
    const isMock = authInstance?.app?.options?.apiKey === 'mock-key';
    if (user && !isMock) {
      await deleteDoc(doc(dbInstance, 'artifacts', appId_va, 'public', 'data', 'assessments', id));
    } else {
      setAssessments(assessments.filter(a => a.id !== id));
    }
  };

  const LoginView = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
      e.preventDefault();
      setLoginLoading(true);
      setError('');
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (err) {
        setError("E-mail ou senha incorretos.");
      } finally {
        setLoginLoading(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-blue-600 p-3 rounded-2xl text-white mb-4">
              <Calendar size={32} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">SchoolSync VA</h1>
            <p className="text-slate-500">Gestão de Varied Assessments</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="email"
                  required
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="password"
                  required
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && <p className="text-sm text-rose-500 text-center">{error}</p>}

            <Button type="submit" className="w-full py-3" disabled={loginLoading}>
              {loginLoading ? <Loader2 className="animate-spin" /> : 'Entrar na Plataforma'}
            </Button>
          </form>
          
          <p className="mt-8 text-center text-xs text-slate-400">
            Acesso Restrito ao Ecossistema Eleva
          </p>
          {propAuth && (
            <div className="mt-4 p-3 bg-amber-50 rounded text-amber-700 text-xs text-center border border-amber-100">
              Sessão integrada do Painel Docente ativa.
            </div>
          )}
        </Card>
      </div>
    );
  };

  const Dashboard = () => {
    const isMock = authInstance?.app?.options?.apiKey === 'mock-key';
    const teacherAssessments = assessments.filter(a => a.teacherId === user?.uid || isMock);
    const confirmedCount = teacherAssessments.filter(a => a.status === 'confirmed').length;
    const pendingCount = teacherAssessments.filter(a => a.status === 'pending_exception').length;

    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Olá, Prof. {user?.displayName || 'Convidado'}!</h1>
            <p className="text-slate-500 text-sm">Gerencie seus Assessments e evite conflitos de carga.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setView('report')}>
              <FileText size={18} /> Gerar PDF
            </Button>
            <Button onClick={() => setView('create')}>
              <Plus size={18} /> Novo Registro
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
              <CheckCircle2 size={24} />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Confirmados</p>
              <p className="text-2xl font-bold text-slate-900">{confirmedCount}</p>
            </div>
          </Card>
          <Card className="flex items-center gap-4">
            <div className="bg-amber-100 p-3 rounded-lg text-amber-600">
              <AlertTriangle size={24} />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Solicitações</p>
              <p className="text-2xl font-bold text-slate-900">{pendingCount}</p>
            </div>
          </Card>
          <Card className="flex items-center gap-4">
            <div className="bg-emerald-100 p-3 rounded-lg text-emerald-600">
              <Calendar size={24} />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Esta Semana</p>
              <p className="text-2xl font-bold text-slate-900">{teacherAssessments.length > 0 ? '3' : '0'}</p>
            </div>
          </Card>
        </div>

        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-lg text-slate-800">Meus Assessments Recentes</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input 
                className="pl-9 pr-4 py-1.5 bg-slate-50 border-none rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Buscar disciplina..."
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b text-xs font-bold text-slate-400 uppercase">
                  <th className="pb-3 px-2">Data/Horário</th>
                  <th className="pb-3 px-2">Turma</th>
                  <th className="pb-3 px-2">Disciplina</th>
                  <th className="pb-3 px-2">Título</th>
                  <th className="pb-3 px-2">Status</th>
                  <th className="pb-3 px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {teacherAssessments.map(a => (
                  <tr key={a.id} className="text-sm hover:bg-slate-50 transition-colors group">
                    <td className="py-4 px-2">
                      <div className="font-medium text-slate-900">{new Date(a.startDate + "T12:00:00").toLocaleDateString('pt-BR')}</div>
                      <div className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {a.startTime || '08:00'}</div>
                    </td>
                    <td className="py-4 px-2">
                       <div className="flex gap-1">
                        {a.classIds?.map(c => <span key={c} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-bold">{c}</span>)}
                       </div>
                    </td>
                    <td className="py-4 px-2 font-medium">{a.subject}</td>
                    <td className="py-4 px-2">
                      <div className="text-slate-800">{a.title}</div>
                      <Badge color={a.weight === 'heavy' ? 'rose' : a.weight === 'medium' ? 'amber' : 'blue'}>
                        {a.weight === 'heavy' ? 'Pesado' : a.weight === 'medium' ? 'Médio' : 'Leve'}
                      </Badge>
                    </td>
                    <td className="py-4 px-2">
                      <Badge color={a.status === 'confirmed' ? 'emerald' : a.status === 'draft' ? 'slate' : 'rose'}>
                        {a.status === 'confirmed' ? 'Confirmado' : a.status === 'draft' ? 'Rascunho' : 'Exceção'}
                      </Badge>
                    </td>
                    <td className="py-4 px-2">
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditingAssessment(a); setView('create'); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit size={16} /></button>
                        <button onClick={() => handleDelete(a.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {teacherAssessments.length === 0 && (
                   <tr>
                     <td colSpan="6" className="py-8 text-center text-slate-400">Nenhum registro encontrado.</td>
                   </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  const ReportView = () => {
    const [selectedTrimester, setSelectedTrimester] = useState(1);
    const [selectedGrade, setSelectedGrade] = useState('6');
    const [selectedClass, setSelectedClass] = useState('6A');

    const classesPool = ['6A', '6B', '6C', '6D', '7A', '7B', '7C', '8A', '8B', '8C', '8D', '9A', '9B', '9C', '9D'];
    const filteredClasses = classesPool.filter(c => c.startsWith(selectedGrade));

    // Ensure selectedClass matches current grade if grade changes
    useEffect(() => {
      if (!selectedClass.startsWith(selectedGrade)) {
        setSelectedClass(filteredClasses[0] || (selectedGrade + 'A'));
      }
    }, [selectedGrade]);

    const handleGenerate = () => {
      const docPDF = new jsPDF('l', 'mm', 'a4');
      const trimester = TRIMESTERS.find(t => t.id === selectedTrimester);
      const filtered = assessments.filter(a => 
        a.status === 'confirmed' && 
        (Array.isArray(a.classIds) && a.classIds.includes(selectedClass)) &&
        a.startDate >= trimester.start && a.startDate <= trimester.end
      );

      // Logo Left
      try {
        docPDF.addImage(LOGO_LEFT, 'PNG', 15, 12, 40, 15);
      } catch(e) {}

      // Left Info
      docPDF.setFontSize(10);
      docPDF.setFont("helvetica", "bold");
      docPDF.setTextColor(30, 41, 59);
      docPDF.text("Segmento: Ensino Fundamental II", 15, 34);
      docPDF.setFont("helvetica", "normal");
      docPDF.text(`Série: ${selectedGrade}º Ano`, 15, 39);
      docPDF.text(`Turma: ${selectedClass}`, 15, 44);
      docPDF.text(`Período: ${trimester.name} / 2026`, 15, 49);

      // Center Title
      docPDF.setFontSize(18);
      docPDF.setFont("helvetica", "bold");
      docPDF.setTextColor(37, 99, 235);
      docPDF.text("Calendário de Varied Assessments", 148, 25, { align: 'center' });
      docPDF.setDrawColor(37, 99, 235);
      docPDF.setLineWidth(1);
      docPDF.line(110, 28, 186, 28);

      // Logo Right
      try {
        docPDF.addImage(LOGO_RIGHT, 'PNG', 242, 10, 40, 18);
      } catch(e) {}

      const tableData = filtered.sort((a,b) => a.startDate.localeCompare(b.startDate)).map(a => [
        new Date(a.startDate + "T12:00:00").toLocaleDateString('pt-BR'),
        (Array.isArray(a.classIds) ? a.classIds.join(', ') : ''),
        a.subject || '',
        a.title || '',
        a.weight === 'heavy' ? 'Pesado' : 'Leve/Médio'
      ]);

      const columns = [['Data', 'Turmas', 'Disciplina', 'Atividade / Natureza', 'Peso']];

      autoTable(docPDF, {
        startY: 55,
        head: columns,
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', halign: 'center' },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { halign: 'center', cellWidth: 30 },
          1: { halign: 'center', cellWidth: 30 },
          4: { halign: 'center', cellWidth: 30 }
        }
      });

      window.open(docPDF.output('bloburl'), '_blank');
    };

    return (
      <div className="max-w-xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setView('dashboard')}><ChevronLeft size={20} /></Button>
          <h1 className="text-2xl font-bold">Relatório de Visibilidade</h1>
        </div>

        <Card className="space-y-6">
          <p className="text-slate-500 text-sm">Selecione o período e a turma para exportar o calendário acadêmico oficial.</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ano Escolar</label>
              <select 
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={selectedGrade}
                onChange={e => setSelectedGrade(e.target.value)}
              >
                <option value="6">6º Ano</option>
                <option value="7">7º Ano</option>
                <option value="8">8º Ano</option>
                <option value="9">9º Ano</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Turma</label>
              <select 
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={selectedClass}
                onChange={e => setSelectedClass(e.target.value)}
              >
                {filteredClasses.map(c => <option key={c} value={c}>Turma {c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Trimestre</label>
              <select 
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={selectedTrimester}
                onChange={e => setSelectedTrimester(Number(e.target.value))}
              >
                {TRIMESTERS.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <Button onClick={handleGenerate} className="w-full py-3">
            <FileText size={18} /> Visualizar PDF Gerado
          </Button>
        </Card>
      </div>
    );
  };

  const CreateAssessment = () => {
    const [formData, setFormData] = useState(() => {
      if (editingAssessment) {
        return {
          ...editingAssessment,
          classIds: editingAssessment.classIds || (editingAssessment.classId ? [editingAssessment.classId] : ['6A'])
        };
      }
      return {
        subject: SUBJECTS[0],
        title: '',
        classIds: ['6A'],
        startDate: '',
        endDate: '',
        startTime: '08:00',
        endTime: '08:50',
        weight: 'medium'
      };
    });
    const [conflicts, setConflicts] = useState({ list: [], canOverflow: true });

    const checkConflictsLogic = (dateStr, classId, weight) => {
      const activeRules = rules.find(r => r.grade === parseInt(classId.substring(0, 1))) || rules[0] || { maxPerDay: 2, maxPerWeek: 4, maxHeavyPerWeek: 1, allowOverflow: true };
      
      // Daily Check
      const dayAssessments = assessments.filter(a => 
        a.id !== editingAssessment?.id &&
        a.status === 'confirmed' &&
        a.classIds?.includes(classId) &&
        dateStr >= a.startDate && dateStr <= a.endDate
      );

      // Weekly Check
      const dateObj = new Date(dateStr + "T12:00:00");
      const dayOfWeek = dateObj.getDay(); 
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(dateObj);
      monday.setDate(dateObj.getDate() + diffToMonday);
      const friday = new Date(monday);
      friday.setDate(monday.getDate() + 4);

      const monStr = monday.toISOString().split('T')[0];
      const friStr = friday.toISOString().split('T')[0];

      const weekAssessments = assessments.filter(a => 
        a.id !== editingAssessment?.id &&
        a.status === 'confirmed' &&
        a.classIds?.includes(classId) &&
        ((a.startDate >= monStr && a.startDate <= friStr) || (a.endDate >= monStr && a.endDate <= friStr))
      );

      const errors = [];
      if (dayAssessments.length >= activeRules.maxPerDay) {
        errors.push(`Limite diário excedido (${activeRules.maxPerDay} por dia)`);
      }
      if (weekAssessments.length >= activeRules.maxPerWeek) {
        errors.push(`Limite semanal excedido (${activeRules.maxPerWeek} por semana)`);
      }
      if (weight === 'heavy') {
        const heavyInWeek = weekAssessments.filter(a => a.weight === 'heavy').length;
        if (heavyInWeek >= activeRules.maxHeavyPerWeek) {
          errors.push(`Limite de avaliações PESADAS excedido (${activeRules.maxHeavyPerWeek} por semana)`);
        }
      }
      
      return { errors, allowOverflow: activeRules.allowOverflow };
    };

    const handleCheck = (currentFormData = formData) => {
      const effectiveStart = currentFormData.startDate || currentFormData.endDate;
      if (!effectiveStart || !currentFormData.classIds || currentFormData.classIds.length === 0) {
        setConflicts({ list: [], canOverflow: true });
        return;
      }

      let newConflicts = [];
      let canOverflow = true;
      const start = new Date(effectiveStart + "T12:00:00");
      const end = currentFormData.endDate ? new Date(currentFormData.endDate + "T12:00:00") : start;

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        
        for (const classId of currentFormData.classIds) {
          const { errors, allowOverflow } = checkConflictsLogic(dateStr, classId, currentFormData.weight);
          if (errors.length > 0) {
            const classErrors = errors.map(err => `[Turma ${classId}] ${err}`);
            newConflicts = [...newConflicts, ...classErrors];
          }
          if (!allowOverflow) canOverflow = false;
        }
      }
      setConflicts({ list: [...new Set(newConflicts)], canOverflow });
    };

    const handleSave = async (status) => {
      if (!formData.title || !formData.endDate || !formData.classIds || formData.classIds.length === 0) return;

      const payload = {
        ...formData,
        startDate: formData.startDate || formData.endDate,
        classIds: formData.classIds,
        classId: formData.classIds[0], // Retrocompatibilidade
        teacherId: user?.uid || 'mock-teacher',
        teacherName: user?.displayName || 'Professor',
        status: status,
        createdAt: new Date().toISOString()
      };

      const isMock = authInstance?.app?.options?.apiKey === 'mock-key';
      if (user && !isMock) {
        if (editingAssessment) {
          await updateDoc(doc(dbInstance, 'artifacts', appId_va, 'public', 'data', 'assessments', editingAssessment.id), payload);
        } else {
          await addDoc(collection(dbInstance, 'artifacts', appId_va, 'public', 'data', 'assessments'), payload);
        }
      } else {
        if (editingAssessment) {
          setAssessments(assessments.map(ass => ass.id === editingAssessment.id ? { ...ass, ...payload } : ass));
        } else {
          setAssessments([...assessments, { id: Date.now().toString(), ...payload }]);
        }
      }
      setEditingAssessment(null);
      setView('dashboard');
    };

    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => { setEditingAssessment(null); setView('dashboard'); }}><ChevronLeft size={20} /></Button>
          <h1 className="text-2xl font-bold">{editingAssessment ? 'Editar Assessment' : 'Novo Assessment'}</h1>
        </div>

        <Card className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Disciplina</label>
              <select
                className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.subject}
                onChange={e => setFormData({ ...formData, subject: e.target.value })}
              >
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Título/Natureza da Atividade</label>
              <input
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Ex: Redação Argumentativa II"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
          </div>

          {/* Seleção de Múltiplas Turmas */}
          <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-semibold text-slate-700">Turmas Vinculadas</label>
              {formData.classIds?.length > 0 && (
                <span className="text-[10px] bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded-full">
                  {formData.classIds.length} {formData.classIds.length === 1 ? 'turma selecionada' : 'turmas selecionadas'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {['6A', '6B', '6C', '6D', '7A', '7B', '7C', '8A', '8B', '8C', '8D', '9A', '9B', '9C', '9D'].map(classId => {
                const isSelected = formData.classIds?.includes(classId);
                return (
                  <button
                    key={classId}
                    type="button"
                    onClick={() => {
                      const current = formData.classIds || [];
                      const newSelection = isSelected 
                        ? current.filter(c => c !== classId)
                        : [...current, classId];
                      const newFormData = { ...formData, classIds: newSelection };
                      setFormData(newFormData);
                      handleCheck(newFormData);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                      isSelected 
                        ? 'bg-blue-600 border-blue-600 text-white shadow-sm scale-105' 
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {classId}
                  </button>
                );
              })}
            </div>
            
            {/* Atalhos Rápidos */}
            <div className="flex flex-wrap gap-4 text-xs font-semibold mt-2 pt-1.5 border-t border-slate-200">
              <button 
                type="button" 
                onClick={() => {
                  const selectedGrades = new Set((formData.classIds || []).map(c => c.substring(0, 1)));
                  if (selectedGrades.size === 0) return;
                  const allMatching = ['6A', '6B', '6C', '6D', '7A', '7B', '7C', '8A', '8B', '8C', '8D', '9A', '9B', '9C', '9D'].filter(c => selectedGrades.has(c.substring(0, 1)));
                  const newFormData = { ...formData, classIds: allMatching };
                  setFormData(newFormData);
                  handleCheck(newFormData);
                }}
                className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
              >
                ⚡ Marcar todas do mesmo ano escolar
              </button>
              <button 
                type="button" 
                onClick={() => {
                  const newFormData = { ...formData, classIds: [] };
                  setFormData(newFormData);
                  handleCheck(newFormData);
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                Limpar Seleção
              </button>
            </div>
          </div>

          {/* Seleção do Peso da Atividade */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Peso</label>
              <select
                className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.weight}
                onChange={e => {
                  const newWeight = e.target.value;
                  const newFormData = { ...formData, weight: newWeight };
                  setFormData(newFormData);
                  handleCheck(newFormData);
                }}
              >
                <option value="light">Leve (Tarefa)</option>
                <option value="medium">Médio (Teste/Quiz)</option>
                <option value="heavy">Pesado (Prova/Projeto)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data de Início (Opcional)</label>
              <input
                type="date"
                className="w-full p-2 border rounded-lg outline-none"
                value={formData.startDate}
                onChange={e => {
                  const newStart = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    startDate: newStart,
                    endDate: prev.endDate || newStart
                  }));
                  setConflicts({ list: [], canOverflow: true });
                }}
                onBlur={handleCheck}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data de Término (Obrigatória)</label>
              <input
                type="date"
                className="w-full p-2 border rounded-lg outline-none"
                value={formData.endDate}
                onChange={e => {
                  const newEnd = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    endDate: newEnd
                  }));
                  setConflicts({ list: [], canOverflow: true });
                }}
                onBlur={handleCheck}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Horário de Início</label>
              <input
                type="time"
                className="w-full p-2 border rounded-lg outline-none"
                value={formData.startTime}
                onChange={e => setFormData({ ...formData, startTime: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Horário de Término</label>
              <input
                type="time"
                className="w-full p-2 border rounded-lg outline-none"
                value={formData.endTime}
                onChange={e => setFormData({ ...formData, endTime: e.target.value })}
              />
            </div>
          </div>

          {(conflicts?.list || []).length > 0 && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-rose-700 font-semibold">
                <AlertTriangle size={18} />
                <span>Conflitos Detectados</span>
              </div>
              <ul className="text-sm text-rose-600 list-disc list-inside">
                {(conflicts?.list || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
              {!conflicts.canOverflow && (
                <p className="text-xs font-bold text-rose-800 bg-rose-100 p-2 rounded">
                  ⚠️ Bloqueio Administrativo: Não é permitido solicitar exceção para este ano escolar.
                </p>
              )}
            </div>
          )}

          {(conflicts?.list || []).length === 0 && formData.endDate && (
            <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center gap-2 text-emerald-700 font-medium">
              <CheckCircle2 size={18} />
              <span>Data disponível para esta turma!</span>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              className="flex-1"
              disabled={(conflicts?.list || []).length > 0 && !conflicts.canOverflow}
              onClick={() => handleSave((conflicts?.list || []).length > 0 ? 'pending_exception' : 'confirmed')}
            >
              {(conflicts?.list || []).length > 0 
                ? (conflicts.canOverflow ? 'Solicitar Exceção' : 'Ação Bloqueada') 
                : 'Confirmar Registro'}
            </Button>
            <Button variant="secondary" onClick={() => handleSave('draft')}>Salvar Rascunho</Button>
          </div>
        </Card>
      </div>
    );
  };

  const AdminDashboard = () => {
    const [activeAdminTrimester, setActiveAdminTrimester] = useState(1);
    const [activeClassFilter, setActiveClassFilter] = useState('Todas');
    const [selectedAdminDate, setSelectedAdminDate] = useState(null);
    const [adminDateAssessments, setAdminDateAssessments] = useState([]);

    const [editingRule, setEditingRule] = useState(null);

    const handleAdminDayClick = (dateStr, assessmentsForDay) => {
      setSelectedAdminDate(dateStr);
      setAdminDateAssessments(assessmentsForDay);
    };

    const handleSaveRule = async (e) => {
      e.preventDefault();
      const updatedRule = {
        ...editingRule,
        maxPerDay: parseInt(editingRule.maxPerDay),
        maxPerWeek: parseInt(editingRule.maxPerWeek),
        maxHeavyPerWeek: parseInt(editingRule.maxHeavyPerWeek)
      };

      const isMock = authInstance?.app?.options?.apiKey === 'mock-key';
      if (user && !isMock) {
        try {
          await setDoc(doc(dbInstance, 'artifacts', appId_va, 'public', 'data', 'rules', updatedRule.id), updatedRule);
        } catch (error) {
          console.error("Erro ao salvar regras no Firestore:", error);
          alert("Não foi possível salvar as regras. Verifique suas permissões.");
        }
      } else {
        setRules(rules.map(r => r.id === updatedRule.id ? updatedRule : r));
      }
      setEditingRule(null);
    };

    const renderHeatmapDays = () => {
      const activeT = TRIMESTERS.find(t => t.id === activeAdminTrimester);
      const start = new Date(activeT.start + "T12:00:00");
      const end = new Date(activeT.end + "T12:00:00");
      const days = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getDay() === 0 || d.getDay() === 6) continue;
        const dateStr = d.toISOString().split('T')[0];
        if (dateStr >= '2026-07-13' && dateStr <= '2026-07-31') continue;
        days.push(dateStr);
      }

      const daysByMonth = {};

      days.forEach(dateStr => {
        const dateObj = new Date(dateStr + "T12:00:00");
        const monthYear = dateObj.toLocaleString('pt-BR', { month: 'short', year: 'numeric' });

        if (!daysByMonth[monthYear]) {
          daysByMonth[monthYear] = [];
        }

        const dayAssessments = assessments.filter(a => {
          if (a.status !== 'confirmed') return false;
          if (activeClassFilter !== 'Todas' && !a.classIds?.includes(activeClassFilter)) return false;
          return dateStr >= a.startDate && dateStr <= a.endDate;
        });
        const count = dayAssessments.length;

        let color = 'bg-slate-100 text-slate-400';
        if (count === 1) color = 'bg-emerald-300 text-emerald-900 border border-emerald-400';
        else if (count === 2) color = 'bg-amber-400 text-amber-900 border border-amber-500';
        else if (count > 2) color = 'bg-rose-500 text-white';

        daysByMonth[monthYear].push({ dateStr, dateObj, count, color, dayAssessments });
      });

      return Object.entries(daysByMonth).map(([monthYear, monthDays]) => (
        <div key={monthYear} className="flex flex-col gap-1.5 mr-4 mb-2">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{monthYear}</span>
          <div className="flex flex-wrap gap-1.5 md:gap-2 max-w-[280px]">
            {monthDays.map(day => (
              <div
                key={day.dateStr}
                onClick={() => handleAdminDayClick(day.dateStr, day.dayAssessments)}
                className={`w-7 h-7 md:w-8 md:h-8 rounded flex items-center justify-center text-[10px] font-medium ${day.color} transition-colors ${day.count > 0 ? 'hover:ring-2 ring-slate-400 cursor-pointer' : 'cursor-default'}`}
                title={`${day.dateObj.toLocaleDateString('pt-BR')}: ${day.count} avaliações`}
              >
                {day.dateObj.getDate()}
              </div>
            ))}
          </div>
        </div>
      ));
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Painel Geral</h1>
            <p className="text-slate-500 text-sm">Visão geral da carga acadêmica e regras.</p>
          </div>
        </div>

        <Card>
          <h3 className="font-semibold mb-6 flex items-center gap-2">
            <Info className="text-blue-600" size={20} /> Regras Vigentes
          </h3>
          <div className="p-4 bg-slate-50 border rounded-lg text-sm text-slate-700 mb-6 italic">
            "Prezados professores: Esta é uma plataforma para organizarmos a quantidade de Varied Assessments ao longo do tempo, dimensionar a carga acadêmica e divulgar o calendario às famílias. Para evitarmos um acúmulo muito grande em uma única semana e diluirmos melhor os trabalhos, é importante observar que não podemos ter mais de 2 avaliações por dia e não ultrapassar 4 avaliações na semana por turma. Pedimos a todos que sigam este padrão."
          </div>
          <div className="space-y-3">
            {rules.map(r => (
              <div key={r.id} className="flex justify-between items-center p-3 bg-white rounded-lg border">
                <div>
                  <span className="font-bold">{r.grade}º Ano</span>
                  <div className="flex gap-4 mt-1">
                    <span className="text-[10px] uppercase text-slate-400">Max/Dia: {r.maxPerDay}</span>
                    <span className="text-[10px] uppercase text-slate-400">Max/Semana: {r.maxPerWeek}</span>
                    <span className="text-[10px] uppercase text-slate-400">Max Pesados: {r.maxHeavyPerWeek}</span>
                    <span className={`text-[10px] uppercase font-bold ${r.allowOverflow ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {r.allowOverflow ? 'Excedente Permitido' : 'Bloqueio de Excedente'}
                    </span>
                  </div>
                </div>
                {isCoordinator 
                  ? <Button variant="secondary" className="text-xs" onClick={() => setEditingRule(r)}>Configurar</Button>
                  : <span className="text-[10px] uppercase text-slate-400 font-medium border border-slate-200 rounded px-2 py-1">Somente Leitura</span>
                }
              </div>
            ))}
          </div>

          {editingRule && isCoordinator && (
            <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-[60]">
              <Card className="w-full max-w-sm shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-lg">Configurar {editingRule.grade}º Ano</h3>
                  <button onClick={() => setEditingRule(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                </div>
                <form onSubmit={handleSaveRule} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">MAX/DIA</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border rounded-lg" 
                        value={editingRule.maxPerDay} 
                        onChange={e => setEditingRule({...editingRule, maxPerDay: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">MAX/SEMANA</label>
                      <input 
                        type="number" 
                        className="w-full p-2 border rounded-lg" 
                        value={editingRule.maxPerWeek} 
                        onChange={e => setEditingRule({...editingRule, maxPerWeek: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">MAX PESADAS/SEMANA</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border rounded-lg" 
                      value={editingRule.maxHeavyPerWeek} 
                      onChange={e => setEditingRule({...editingRule, maxHeavyPerWeek: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">Permitir Solicitação de Exceção</span>
                    <button 
                      type="button"
                      onClick={() => setEditingRule({...editingRule, allowOverflow: !editingRule.allowOverflow})}
                      className={`w-10 h-6 rounded-full transition-colors relative ${editingRule.allowOverflow ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${editingRule.allowOverflow ? 'right-1' : 'left-1'}`}></div>
                    </button>
                  </div>
                  <Button type="submit" className="w-full py-3">Salvar Configuração</Button>
                </form>
              </Card>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="text-blue-600" size={20} /> Heatmap de Avaliações
            </h3>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <select
                className="p-1.5 text-sm border-slate-200 border outline-none rounded-md bg-white text-slate-700 shadow-sm"
                value={activeClassFilter}
                onChange={e => setActiveClassFilter(e.target.value)}
              >
                <option value="Todas">Todas as Turmas</option>
                {['6A', '6B', '6C', '6D', '7A', '7B', '7C', '8A', '8B', '8C', '8D', '9A', '9B', '9C', '9D'].map(c => (
                  <option key={c} value={c}>Turma {c}</option>
                ))}
              </select>
              <div className="flex bg-slate-100 p-1 rounded-lg self-start md:self-auto overflow-x-auto max-w-full">
                {TRIMESTERS.map(t => (
                  <button
                    key={'admin-trim-' + t.id}
                    onClick={() => setActiveAdminTrimester(t.id)}
                    className={`px-4 py-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-all ${activeAdminTrimester === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-start items-start p-6 bg-slate-50 border border-slate-100 rounded-xl">
            {renderHeatmapDays()}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-slate-500 justify-center">
            <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-slate-100"></div> Livre</div>
            <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-emerald-300 border border-emerald-400"></div> 1 Avaliação</div>
            <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-amber-400 border border-amber-500"></div> 2 Avaliações (Limite)</div>
            <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-rose-500"></div> {'>'}2 Avaliações (Excesso)</div>
          </div>

          {selectedAdminDate && (
            <div className="fixed inset-0 bg-slate-900/50 flex flex-col items-center justify-center p-4 z-50">
              <Card className="w-full max-w-md shadow-2xl relative">
                <button
                  onClick={() => setSelectedAdminDate(null)}
                  className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={24} />
                </button>

                <div className="mb-4 pr-8">
                  <h3 className="font-bold text-xl text-slate-900 flex items-center gap-2">
                    <Calendar size={24} className="text-blue-600" />
                    {new Date(selectedAdminDate + "T12:00:00").toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">
                    {adminDateAssessments.length} {adminDateAssessments.length === 1 ? 'avaliação registrada' : 'avaliações registradas'} neste dia.
                  </p>
                </div>

                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                  {adminDateAssessments.map(a => (
                    <div key={a.id} className="p-3 rounded-lg border bg-slate-50">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-sm text-slate-800">{a.title}</h4>
                        <Badge color={a.weight === 'heavy' ? 'rose' : 'blue'}>
                          {a.weight === 'heavy' ? 'Pesado' : 'Leve/Médio'}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                        <Clock size={12} />
                        <span>{a.startTime} às {a.endTime}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {a.classIds?.map(c => (
                          <span key={c} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">Turma {c}</span>
                        ))}
                      </div>
                      <div className="mt-2 text-xs font-semibold text-slate-600 bg-white inline-block px-2 py-1 rounded border">
                        Prof. {a.teacherName}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </Card>
      </div>
    );
  };

  if (loading) return <div className="h-screen flex items-center justify-center text-slate-400">Carregando plataforma...</div>;

  const isMock = authInstance?.app?.options?.apiKey === 'mock-key';
  if (!user && !isMock) return <LoginView />;

  return (
    <div className={`min-h-[500px] bg-slate-50 flex flex-col md:flex-row ${!propSession ? 'min-h-screen' : ''}`}>
      <nav className={`w-full md:w-64 bg-white border-r border-slate-200 flex flex-col p-4 ${propSession ? 'rounded-l-2xl' : ''}`}>
        <div className="flex items-center gap-3 mb-10 px-2 text-blue-600">
          <div className="bg-blue-600 p-2 rounded-lg text-white">
            <Calendar size={20} />
          </div>
          <span className="font-bold text-lg tracking-tight text-slate-900">SchoolSync VA</span>
        </div>

        <div className="space-y-2 flex-1">
          {!isCoordinator && (
            <button
              onClick={() => setView('dashboard')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'dashboard' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <LayoutDashboard size={18} /> Meu Painel
            </button>
          )}
          <button
            onClick={() => setView('admin')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'admin' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <ShieldCheck size={18} /> Painel Geral
          </button>
          <button
            onClick={() => setView('report')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'report' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <FileText size={18} /> Relatórios
          </button>
        </div>

        <div className="pt-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-4">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs uppercase">
              {user?.displayName?.substring(0, 1) || 'P'}
            </div>
            <div className="overflow-hidden">
              <p className="text-xs font-bold truncate">{user?.displayName || 'Professor'}</p>
              <p className="text-[10px] text-slate-400 truncate">{user?.email || 'convidado@eleva.com'}</p>
            </div>
          </div>
          {user && !propSession && (
            <button onClick={() => signOut(authInstance)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors">
              <LogOut size={18} /> Sair
            </button>
          )}
          {propSession && (
            <div className="px-3 py-2 text-[10px] text-slate-400 text-center italic">
              Sessão Gerenciada pelo Sistema
            </div>
          )}
        </div>
      </nav>

      <main className="flex-1 p-4 md:p-8 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          {view === 'dashboard' && <Dashboard />}
          {view === 'create' && <CreateAssessment />}
          {view === 'admin' && <AdminDashboard />}
          {view === 'report' && <ReportView />}
        </div>
      </main>
    </div>
  );
}

export default SchoolSyncVA;
